import React, { useCallback, useState } from "react";
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardContent,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  IonDatetime,
  IonModal,
  IonButtons,
  IonButton,
  RefresherEventDetail,
  IonList,
  IonListHeader,
} from "@ionic/react";
import {
  calendarOutline,
  chevronBackOutline,
  chevronForwardOutline,
} from "ionicons/icons";
import { useQuery } from "@tanstack/react-query";
import { getPlanning, getWeekPlanning, Planning, Timesheet } from "@/api";
import "./Planning.css";
import DayPlanningCard from "@components/DayPlanningCard.tsx";
import GroupedDayPlanningCard from "@components/GroupedDayPlanningCard.tsx";
import EditTimesheetModal from "@components/Modals/EditTimesheetModal.tsx";
import { AppHeader } from "@/components/AppHeader";

type ViewMode = "day" | "week";

const PlanningPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("day");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [showEditTimesheet, setShowEditTimesheet] = useState<boolean>(false);
  const [createdTimesheet, setCreatedTimesheet] = useState<
    Timesheet | undefined
  >(undefined);

  const handleEnterHours = useCallback((planning?: Planning) => {
    if (!planning) return;

    const timesheet: Timesheet = {
      created_at: "",
      source: "mobile",
      updated_at: "",
      date: planning.date,
      hours: 0,
      project_id: planning.project_id,
      project: planning.project,
    };

    setCreatedTimesheet(timesheet);
    setShowEditTimesheet(true);
  }, []);

  const handleCloseTimesheet = useCallback(() => {
    setShowEditTimesheet(false);
    setCreatedTimesheet(undefined);
  }, []);

  const {
    data: dayPlanning,
    isLoading: isDayLoading,
    refetch: refetchDay,
  } = useQuery({
    queryKey: ["planning", "day", selectedDate],
    queryFn: () => getPlanning(selectedDate),
    enabled: viewMode === "day",
  });

  const {
    data: weekPlanning,
    isLoading: isWeekLoading,
    refetch: refetchWeek,
  } = useQuery({
    queryKey: ["planning", "week", selectedDate],
    queryFn: () => getWeekPlanning(selectedDate),
    enabled: viewMode === "week",
  });

  const isLoading = viewMode === "day" ? isDayLoading : isWeekLoading;
  const planning = viewMode === "day" ? dayPlanning : weekPlanning;

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    if (viewMode === "day") {
      await refetchDay();
    } else {
      await refetchWeek();
    }
    event.detail.complete();
  };

  const navigateDate = (direction: "prev" | "next") => {
    const newDate = new Date(selectedDate);
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
    } else {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const formatDateHeader = () => {
    if (viewMode === "day") {
      const isToday = selectedDate.toDateString() === new Date().toDateString();
      const dayName = selectedDate.toLocaleDateString("nl-NL", {
        weekday: "long",
      });
      const date = selectedDate.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return isToday ? `Vandaag - ${date}` : `${dayName}, ${date}`;
    } else {
      const monday = new Date(selectedDate);
      monday.setDate(selectedDate.getDate() - selectedDate.getDay() + 1);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      return `Week ${getWeekNumber(
        selectedDate
      )} - ${monday.getDate()} ${monday.toLocaleDateString("nl-NL", {
        month: "short",
      })} - ${sunday.getDate()} ${sunday.toLocaleDateString("nl-NL", {
        month: "short",
      })}`;
    }
  };

  const getWeekNumber = (date: Date) => {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };

  const groupPlanningByDate = (planning: Planning[]) => {
    const grouped: { [key: string]: Planning[] } = {};
    planning?.forEach((item) => {
      if (!grouped[item.date]) {
        grouped[item.date] = [];
      }
      grouped[item.date].push(item);
    });
    return grouped;
  };

  const groupedPlanning =
    viewMode === "week" ? groupPlanningByDate(planning || []) : null;

  return (
    <IonPage>
      <AppHeader title="Planning" />

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <EditTimesheetModal
          timesheet={createdTimesheet}
          showModal={showEditTimesheet}
          handleClose={() => handleCloseTimesheet()}
        />

        <div className="planning-container">
          {/* View Mode Selector */}
          <IonSegment
            color="primary"
            value={viewMode}
            onIonChange={(e) => setViewMode(e.detail.value as ViewMode)}
          >
            <IonSegmentButton value="day">
              <IonLabel>Dag</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="week">
              <IonLabel>Week</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Date Navigation */}
          <IonCard className="date-nav-card">
            <IonCardContent>
              <div className="date-navigation">
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => navigateDate("prev")}
                >
                  <IonIcon icon={chevronBackOutline} />
                </IonButton>

                <div
                  className="date-display"
                  onClick={() => setShowDatePicker(true)}
                >
                  <h3>{formatDateHeader()}</h3>
                </div>

                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => navigateDate("next")}
                >
                  <IonIcon icon={chevronForwardOutline} />
                </IonButton>
              </div>

              <IonButton
                expand="block"
                fill="clear"
                size="small"
                onClick={goToToday}
                className="today-button"
              >
                Ga naar vandaag
              </IonButton>
            </IonCardContent>
          </IonCard>

          {/* Planning Content */}
          {isLoading ? (
            <IonCard>
              <IonCardContent>
                <IonSkeletonText
                  animated
                  style={{ height: "100px", marginBottom: "10px" }}
                />
                <IonSkeletonText
                  animated
                  style={{ height: "100px", marginBottom: "10px" }}
                />
                <IonSkeletonText animated style={{ height: "100px" }} />
              </IonCardContent>
            </IonCard>
          ) : viewMode === "day" ? (
            // Day View
            dayPlanning?.length === 0 ? (
              <IonCard>
                <IonCardContent className="empty-state">
                  <IonIcon icon={calendarOutline} />
                  <p>Geen planning voor deze dag</p>
                </IonCardContent>
              </IonCard>
            ) : (
              <IonCard>
                <IonCardContent>
                  <IonList>
                    <IonListHeader>
                      <IonLabel>
                        {new Date().toLocaleDateString("nl-NL", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        })}
                      </IonLabel>
                    </IonListHeader>
                    {dayPlanning?.map((item) => (
                      <DayPlanningCard
                        item={item}
                        key={item.id}
                        onClick={() => handleEnterHours(item)}
                      />
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )
          ) : // Week View
          Object.keys(groupedPlanning || {}).length === 0 ? (
            <IonCard>
              <IonCardContent className="empty-state">
                <IonIcon icon={calendarOutline} />
                <p>Geen planning voor deze week</p>
              </IonCardContent>
            </IonCard>
          ) : (
            <IonCard>
              <IonCardContent>
                {Object.entries(groupedPlanning || {})
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([date, items]) => (
                    <GroupedDayPlanningCard
                      key={date}
                      date={date}
                      items={items}
                    />
                  ))}
              </IonCardContent>
            </IonCard>
          )}
        </div>

        {/* Date Picker Modal */}
        <IonModal
          isOpen={showDatePicker}
          onDidDismiss={() => setShowDatePicker(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Selecteer datum</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowDatePicker(false)}>
                  Sluiten
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonDatetime
              presentation="date"
              value={selectedDate.toISOString()}
              onIonChange={(e) => {
                if (e.detail.value) {
                  setSelectedDate(new Date(e.detail.value as string));
                  setShowDatePicker(false);
                }
              }}
            />
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default PlanningPage;
